import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { isDateAllowedByScheduleOverview, toLocalDateString, type ScheduleOverviewRules } from "@/lib/schedule-overview";
import { maxBookableDate, type BookingHorizonDays, DEFAULT_BOOKING_HORIZON_DAYS } from "@/lib/booking-horizon";
import { todayInZone } from "@/lib/timezone";

const DAY_NAMES = ["Pon", "Uto", "Sri", "Čet", "Pet", "Sub", "Ned"];
const MONTH_NAMES = [
  "Januar", "Februar", "Mart", "April", "Maj", "Juni",
  "Juli", "August", "Septembar", "Oktobar", "Novembar", "Decembar",
];

type ScheduleOverview = ScheduleOverviewRules;

/**
 * "Today" for the booking grid: the Sarajevo calendar date (the one the server
 * uses for lead time and horizon), held as a local-midnight Date that is only a
 * (year, month, day) container. Every grid comparison reads local components
 * (toLocalDateString / getDay), so a client in New York or Tokyo sees the same
 * bookable days the server will accept.
 */
export function bookingToday(): Date {
  const [y, m, d] = todayInZone().split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

export function CalendarGrid({
  therapistId,
  currentMonth,
  currentYear,
  today,
  selectedDate,
  canGoPrev,
  emergencyDates,
  onPrevMonth,
  onNextMonth,
  onSelectDate,
}: {
  therapistId: string;
  currentMonth: number;
  currentYear: number;
  today: Date;
  selectedDate: Date | null;
  canGoPrev: boolean;
  /** Dates (YYYY-MM-DD) inside the lead window that offer emergency slots. */
  emergencyDates?: Set<string>;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectDate: (date: Date) => void;
}) {
  const { t } = useTranslation();
  const { data: scheduleOverview, isError, refetch } = useQuery<ScheduleOverview>({
    queryKey: ["schedule-overview", therapistId],
    queryFn: async () => {
      const res = await fetch(`/api/therapists/${therapistId}/schedule-overview`);
      // Throw on HTTP error so a failed load surfaces as an error state instead
      // of silently disabling every date (which reads as "nothing available").
      if (!res.ok) throw new Error("schedule-overview");
      return res.json();
    },
  });

  const rules: ScheduleOverviewRules = scheduleOverview ?? {
    availableDays: [],
    blockedDates: [],
    addedDates: [],
    bookingHorizonDays: DEFAULT_BOOKING_HORIZON_DAYS,
    minBookingLeadDays: 2,
  };
  const maxDate = useMemo(() => {
    return maxBookableDate(rules.bookingHorizonDays as BookingHorizonDays | null, today);
  }, [rules.bookingHorizonDays, today]);

  const isDateAvailable = (date: Date) => {
    return isDateAllowedByScheduleOverview(date, today, rules);
  };

  const canGoNext = useMemo(() => {
    if (!maxDate) return true;
    // First day of next month in the user's local timezone:
    const firstOfNext = new Date(currentYear, currentMonth + 1, 1);
    return toLocalDateString(firstOfNext) <= maxDate;
  }, [maxDate, currentMonth, currentYear]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    let startDow = firstDay.getDay() - 1;
    if (startDow < 0) startDow = 6;

    const days: Array<{ date: Date; inMonth: boolean }> = [];
    for (let i = startDow - 1; i >= 0; i--) {
      days.push({ date: new Date(currentYear, currentMonth, -i), inMonth: false });
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push({ date: new Date(currentYear, currentMonth, d), inMonth: true });
    }
    while (days.length < 42) {
      days.push({ date: new Date(currentYear, currentMonth + 1, days.length - lastDay.getDate() - startDow + 1), inMonth: false });
    }
    return days;
  }, [currentMonth, currentYear]);

  // Compare calendar-date strings, the same values earliestBookableDate /
  // maxBookableDate return, never Date instants.
  const todayYmd = toLocalDateString(today);
  const selectedYmd = selectedDate ? toLocalDateString(selectedDate) : null;
  const isToday = (d: Date) => toLocalDateString(d) === todayYmd;
  const isPast = (d: Date) => toLocalDateString(d) < todayYmd;
  const isSelected = (d: Date) => toLocalDateString(d) === selectedYmd;

  if (isError) {
    return (
      <div className="rounded-xl bg-muted/20 py-8 text-center">
        <p className="text-sm text-muted-foreground">{t("booking.datesLoadError")}</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-2 text-sm font-medium text-primary underline underline-offset-4"
        >
          {t("booking.retry")}
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Month nav */}
      <div className="flex items-center justify-between">
        <Button data-testid="booking-cal-prev" variant="ghost" size="icon-sm" onClick={onPrevMonth} disabled={!canGoPrev}>
          <CaretLeft className="size-4" />
        </Button>
        <span data-testid="booking-month-label" className="text-sm font-bold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
          {MONTH_NAMES[currentMonth]} {currentYear}
        </span>
        <Button data-testid="booking-cal-next" variant="ghost" size="icon-sm" onClick={onNextMonth} disabled={!canGoNext}>
          <CaretRight className="size-4" />
        </Button>
      </div>

      <div className="mt-1 grid grid-cols-7">
        {DAY_NAMES.map((d) => (
          <div key={d} className="py-1.5 text-center text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {calendarDays.map(({ date, inMonth }, i) => {
          const past = isPast(date);
          const ymd = toLocalDateString(date);
          // Emergency slots live inside the lead window, on dates the regular
          // rules disable; those dates stay bookable and are marked amber.
          const urgent = !past && (emergencyDates?.has(ymd) ?? false);
          const available = isDateAvailable(date) || urgent;
          const disabled = !inMonth || past || !available;
          const sel = isSelected(date);
          const tod = isToday(date);
          return (
            <button
              key={i}
              data-testid={inMonth ? `booking-day-${ymd}` : undefined}
              data-available={inMonth && !past && available ? "true" : "false"}
              data-emergency={inMonth && urgent ? "true" : undefined}
              onClick={() => { if (!disabled) onSelectDate(date); }}
              disabled={disabled}
              className={cn(
                "relative flex aspect-square items-center justify-center rounded-xl text-[13px] font-medium transition-all",
                disabled ? "cursor-default text-muted-foreground/30" : "cursor-pointer hover:bg-muted/60",
                sel && "bg-foreground text-background shadow-sm hover:bg-foreground",
                tod && !sel && available && "ring-1 ring-inset ring-border",
                !inMonth && "invisible",
                inMonth && !past && available && !sel && "text-foreground",
              )}
            >
              {date.getDate()}
              {inMonth && !past && available && !sel && (
                <span className={cn(
                  "absolute bottom-1.5 left-1/2 size-1 -translate-x-1/2 rounded-full",
                  urgent ? "bg-amber-500" : "bg-primary",
                )} />
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}
